import React, { useState } from 'react'
import { Modal, Button, message, Progress, Spin, Typography, Alert } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import axios from 'axios'

const { Text, Title } = Typography

interface SchemaEditorProps {
  datasetId: string
  datasetName: string
  initialSchema: string
  visible: boolean
  onClose: (refreshNeeded: boolean) => void
}

const SchemaEditor: React.FC<SchemaEditorProps> = ({ 
  datasetId, 
  datasetName,
  initialSchema,
  visible, 
  onClose 
}) => {
  const [schema, setSchema] = useState(initialSchema)
  const [regenerating, setRegenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('')
  const [eventSource, setEventSource] = useState<EventSource | null>(null)

  const handleSaveAndRegenerate = async () => {
    if (!schema.trim()) {
      message.error('Schema cannot be empty!')
      return
    }

    setRegenerating(true)
    setProgress(0)
    setStage('Starting...')

    try {
      // Start regeneration
      const response = await axios.put(
        `http://localhost:5000/api/datasets/${datasetId}/schema`,
        { schema, regenerate: true }
      )

      const { job_id } = response.data

      // Connect to SSE stream for progress
      const es = new EventSource(
        `http://localhost:5000/api/datasets/${datasetId}/regenerate/${job_id}/progress`
      )

      setEventSource(es)

      es.onmessage = (event) => {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'queued':
            setStage('Waiting to start...')
            break

          case 'progress':
            setProgress(data.percent)
            setStage(data.stage || 'Processing...')
            break

          case 'complete':
            setProgress(100)
            setStage('Complete!')
            message.success(
              `Dataset regenerated successfully! ${data.count} training examples created.`
            )
            es.close()
            setTimeout(() => {
              setRegenerating(false)
              onClose(true) // Trigger refresh
            }, 1500)
            break

          case 'error':
            message.error(`Regeneration failed: ${data.message}`)
            es.close()
            setRegenerating(false)
            break
        }
      }

      es.onerror = () => {
        message.error('Connection to server lost')
        es.close()
        setRegenerating(false)
      }

    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to start regeneration')
      setRegenerating(false)
    }
  }

  const handleCancel = () => {
    if (eventSource) {
      eventSource.close()
    }
    onClose(false)
  }

  return (
    <Modal
      title={
        <>
          <Title level={4} style={{ margin: 0 }}>Edit Schema & Regenerate Dataset</Title>
          <Text type="secondary">{datasetName}</Text>
        </>
      }
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={regenerating ? null : [
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={handleSaveAndRegenerate}
        >
          Save & Regenerate Dataset
        </Button>
      ]}
      maskClosable={false}
    >
      {regenerating ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Progress 
              percent={progress} 
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <Text style={{ marginTop: '16px', display: 'block', fontSize: '15px' }}>
              {stage}
            </Text>
            {progress > 0 && progress < 100 && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                This may take a few minutes...
              </Text>
            )}
          </div>
        </div>
      ) : (
        <>
          <Alert
            message="Modifying the schema will regenerate the entire training dataset"
            description="The old schema will be backed up. You can restore it if needed. Training examples will be regenerated based on the new schema."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          
          <textarea
            value={schema}
            onChange={(e) => setSchema(e.target.value)}
            style={{
              width: '100%',
              height: '500px',
              padding: '16px',
              fontSize: '13px',
              lineHeight: '1.5',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              resize: 'vertical'
            }}
            placeholder="Paste your YAML schema here..."
          />
        </>
      )}
    </Modal>
  )
}

export default SchemaEditor
